/**
 * KVS Samagam API Service (Legacy)
 * 
 * These async functions use apiClient (axios) which has 401 interceptor.
 * RTK Query equivalents are available in: store/api/kvsSamagamApi.ts
 * 
 * For new code, prefer RTK Query hooks:
 *   import { useGetSamagamTotalSchoolsQuery } from '@/store/api';
 * 
 * These functions are kept for backward compatibility with useKPIData and analytics screens.
 */

import { apiGet } from '../../../shared/services/apiClient';

// Returns the schoolCount value from the API response, matching dashboard logic
export async function getTotalSchools() {
  const data = await apiGet('/kvis/total');
  if (data && data.success && data.data && typeof data.data.schoolCount === 'number') {
    return data.data.schoolCount;
  }
  return null;
}

// Fetches all states for dropdowns
export async function getAllStates() {
  const data = await apiGet('/shared/allstate');
  if (data && data.success && Array.isArray(data.data)) {
    return data.data;
  }
  return [];
}

// Fetches all regions for dropdowns (excluding "Abroad KV")
export async function getAllRegions() {
  const data = await apiGet('/shared/allros');
  if (data && data.success && Array.isArray(data.data)) {
    // Filter out "Abroad KV" region
    return data.data.filter((region: any) => region.roName !== 'Abroad KV');
  }
  return [];
}

// Fetches all categories for dropdowns
export async function getAllCategories(): Promise<Array<{ key: string; label: string }>> {
  try {
    const data = await apiGet('/shared/category');
    console.log('Category API response:', data); // Debug log
    if (data && data.success && data.data && typeof data.data === 'object') {
      // The API returns an object where values can be strings or objects
      return Object.entries(data.data)
        .map(([index, value]) => {
          let profileName: string;
          let displayLabel: string;

          // Handle both string values and object values
          if (typeof value === 'string') {
            profileName = value;
            // Create a readable label from the profile name
            displayLabel = value
              .replace(/([A-Z])/g, ' $1') // Add space before capital letters
              .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
              .trim();
          } else if (value && typeof value === 'object') {
            // If it's an object, extract the profile name and label
            const obj = value as Record<string, any>;
            profileName = obj.key || obj.name || obj.value || obj.profileName || '';
            displayLabel = obj.label || obj.displayName || profileName;
          } else {
            return null; // Skip invalid entries
          }

          // Only return valid entries with non-empty profile names
          if (!profileName) {
            return null;
          }

          return {
            key: profileName,
            label: displayLabel
          };
        })
        .filter((item): item is { key: string; label: string } => item !== null); // Remove null entries
    }
    return [];
  } catch (error) {
    // console.error('Error in getAllCategories:', error);
    return [];
  }
}

// Fetch ROwise data for KVS Samagam dashboard
export async function getKvsSamagamRowiseData() {
  const data = await apiGet('/kvis/kvprofile/rostoalldistrictstoallkvs');
  if (data && data.success && Array.isArray(data.data)) {
    return data.data;
  }
  return [];
}

// Get region information for a specific district using API data
export async function getRegionForDistrictFromAPI(districtName: string): Promise<{ regionName: string; regionColor: string; stateName: string } | null> {
  try {
    const data = await apiGet('/kvis/kvprofile/rostoalldistrictstoallkvs');

    if (!data || !data.success || !Array.isArray(data.data)) {
      return null;
    }

    const normalizeDistrictName = (name: string): string => {
      return name.toUpperCase()
        .replace(/\s+/g, '')
        .replace(/[.-]/g, '')
        .replace(/\(/g, '')
        .replace(/\)/g, '')
        .replace(/&/g, 'AND')
        .replace(/'/g, '')
        .trim();
    };

    const normalizedSearchName = normalizeDistrictName(districtName);

    // Search through all regions and their districts
    for (const region of data.data) {
      const foundDistrict = region.districts?.find((d: any) => {
        const normalizedDistrictName = normalizeDistrictName(d.districtName);
        return normalizedDistrictName === normalizedSearchName ||
          normalizedDistrictName.includes(normalizedSearchName) ||
          normalizedSearchName.includes(normalizedDistrictName);
      });

      if (foundDistrict) {
        return {
          regionName: region['RO name'],
          regionColor: region.color || '#4ECDC4',
          stateName: region.state
        };
      }
    }

    return null;
  } catch (error) {
    console.error('Error getting region for district from API:', error);
    return null;
  }
}

// Get region information for a specific district
export async function getRegionForDistrict(districtName: string): Promise<{ regionName: string; regionColor: string } | null> {
  try {
    // This could be enhanced to call an API, but for now we'll use the static regions data
    const response = await fetch('/data/regions.json');
    const regionsData = await response.json();

    const normalizeDistrictName = (name: string): string => {
      return name.toUpperCase()
        .replace(/\s+/g, '')
        .replace(/[.-]/g, '')
        .replace(/\(/g, '')
        .replace(/\)/g, '')
        .replace(/&/g, 'AND')
        .replace(/'/g, '')
        .trim();
    };

    const region = regionsData.regions.find((r: any) => {
      return r.districts.some((d: string) => {
        const normalized1 = normalizeDistrictName(districtName);
        const normalized2 = normalizeDistrictName(d);
        return normalized1 === normalized2 ||
          normalized1.includes(normalized2) ||
          normalized2.includes(normalized1);
      });
    });

    return region ? { regionName: region.name, regionColor: region.color } : null;
  } catch (error) {
    console.error('Error getting region for district:', error);
    return null;
  }
}

// Returns an array of { schoolName, latitude, longitude, roName, stateName, districtName } for all schools in all regions
export async function getAllSchoolCoordinates() {
  const data = await apiGet('/kvis/kvprofile/rostoalldistrictstoallkvs');
  if (data && data.success && Array.isArray(data.data)) {
    const schools: { schoolID?: number; schoolName: string; latitude: number; longitude: number; roName?: string; stateName?: string; districtName?: string; isPMShree?: number }[] = [];
    data.data.forEach((region: any) => {
      region.districts?.forEach((district: any) => {
        district.kvsDetails?.forEach((school: any) => {
          if (school.latitude && school.longitude) {
            schools.push({
              schoolID: school.schoolID || school.id || school.kvID,
              schoolName: school.schoolName,
              latitude: Number(school.latitude),
              longitude: Number(school.longitude),
              roName: school.roName || region['RO name'] || region.roName || '',
              stateName: school.stateName || region.state || '',
              districtName: school.districtName || district.districtName || '',
              isPMShree: Number(school.isPMShree) || 0,
            });
          }
        });
      });
    });
    return schools;
  }
  return [];
}

// state name and school count with school details
export async function getSchoolNameAndCount(stateId?: number) {
  const data = await apiGet('/kvis/statewisekvisdetails');
  if (data && data.success && data.data && Array.isArray(data.data.data)) {
    return data.data.data.map((state: any) => {
      // Filter out schools where schoolName is null or empty
      const validSchools = (state.kvisDetails || []).filter((school: any) =>
        school.schoolName && school.schoolName.trim() !== ''
      );

      return {
        stateID: state.stateDetails?.stateID,
        stateName: state.stateDetails?.stateName,
        schoolCount: validSchools.length, // Update count to reflect only valid schools
        kvisDetails: validSchools
      };
    });
  }
  return [];
}

// get kpi options from selected category
export async function getKPIsByCategory(categorykey: string): Promise<Array<{ key: string; label: string }>> {
  try {
    const data = await apiGet(`/shared/kpicategorybyprofile?category=${categorykey}`);
    // console.log('KPI API response:', data); // Debug log

    if (data && data.success) {
      // Handle array response (old API format)
      if (Array.isArray(data.data)) {
        return data.data.map((item: any) => ({
          key: item.kpiKey,
          label: item.kpiLabel
        }));
      }

      // Handle object response (new API format)
      if (data.data && typeof data.data === 'object') {
        return Object.entries(data.data).map(([key, label]) => ({
          key,
          label: String(label)
        }));
      }
    }
  } catch (error) {
    console.error('Error fetching KPI options:', error);
  }
  return [];
}

// get kpi data for selected category and kpi
export async function getKPIData(categoryKey: string, kpiKey: string): Promise<any[] | null> {
  const data = await apiGet(`/kvis/kvprofile/kpicatdatabyprofile?category=${categoryKey}&kpi=${kpiKey}`);
  if (data && data.success) {
    return data.data;
  }
  return null;
}

// Fetch full school details by schoolID
export async function getSchoolFullDetails(schoolID: string | number) {
  const data = await apiGet(`/kvis/schoolsfulldetail/${schoolID}`);
  if (data && data.success && data.data) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch school full details');
}

// Infrastructure Analytics APIs
export async function getTotalClassrooms() {
  const data = await apiGet('/kvis/totalclassroomskvs');
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch total classrooms');
}

export async function getTotalLaboratories() {
  const data = await apiGet('/kvis/totallaboratoriessciencecomputerlabs');
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch total laboratories');
}

export async function getTotalLibraries() {
  const data = await apiGet('/kvis/totallibraries');
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch total libraries');
}

export async function getTotalMultipurposeHalls() {
  const data = await apiGet('/kvis/totalmultipurposehallsassemblyevents');
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch total multipurpose halls');
}

export async function getClassroomsPmShriComparison() {
  const data = await apiGet('/kvis/classrooms-pmshri-nonpmshri');
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch PM-SHRI comparison data');
}

export async function getTopSchoolsByInfra(wise: string = 'classroomsinfra', page: number = 1) {
  const isInfraFolio = ['adminrooms', 'supportfacilities', 'quarters'].includes(wise);
  const baseUrl = isInfraFolio ? '/infrafolio' : '/kvis';
  const data = await apiGet(`${baseUrl}/topschools-infra?wise=${wise}&order=desc&page=${page}`);
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch top schools by infrastructure');
}

export async function getSchoolsNeedingAttention(wise: string = 'classroomsinfra', page: number = 1) {
  const isInfraFolio = ['adminrooms', 'supportfacilities', 'quarters'].includes(wise);
  const baseUrl = isInfraFolio ? '/infrafolio' : '/kvis';
  const data = await apiGet(`${baseUrl}/topschools-infra?wise=${wise}&order=asc&page=${page}`);
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch schools needing attention');
}

export async function getTotalAdministrativeRooms() {
  const data = await apiGet('/infrafolio/totaladministrativerooms');
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch total administrative rooms');
}

export async function getTotalSupportFacilities() {
  const data = await apiGet('/infrafolio/totalsupportfacilities');
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch total support facilities');
}

export async function getTotalStaffQuarters() {
  const data = await apiGet('/infrafolio/totalstaffquarters');
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch total staff quarters');
}

// Enrollment APIs
export async function getClasswiseEnrollment() {
  const data = await apiGet('/kvis/classwiseenrollpm-nonpmshri');
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch classwise enrollment');
}

export async function getClasswiseEnrollmentPercentage() {
  const data = await apiGet('/kvis/classwiseenrollpm-nonpmshriperc');
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch classwise enrollment percentage');
}

export async function getEnrollmentTrend(years: number = 5) {
  const data = await apiGet(`/kvis/enrollmenttrend-lastyears?year=${years}`);
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch enrollment trend');
}

export async function getClasswiseHighestEnrollment(years: number = 5) {
  const data = await apiGet(`/kvis/classwisehighestenrollment?year=${years}`);
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch classwise highest enrollment');
}

export async function getHighestEnrollmentSchools(wise: string = 'classwiseenrollment', order: string = 'desc') {
  const data = await apiGet(`/kvis/highestenrollmentschools?wise=${wise}&order=${order}`);
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch highest enrollment schools');
}

export async function getLowEnrollmentSchools(wise: string = 'classwiseenrollment') {
  const data = await apiGet(`/kvis/highestenrollmentschools?wise=${wise}&order=asc`);
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch low enrollment schools');
}

export async function getParentServiceGroupData() {
  const data = await apiGet('/kvis/parentservicegroupdata');
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch parent service group data');
}

export async function getCastConseGroupData() {
  const data = await apiGet('/kvis/castconsesgroupdata');
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch caste/social category group data');
}

export async function getMinorityGroupData() {
  const data = await apiGet('/kvis/castminoritygroupdata');
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch minority group data');
}

// ICT Profile APIs
export async function getTotalComputersICT() {
  const data = await apiGet('/kvis/totalcomputers-ict');
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch total computers');
}

export async function getIctComputerLabs() {
  const data = await apiGet('/kvis/ictcomputerlab');
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch ICT computer labs');
}

export async function getEDigiLearningRooms() {
  const data = await apiGet('/kvis/edigilearningrooms');
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch e-learning rooms');
}

export async function getInteractivePanels() {
  const data = await apiGet('/kvis/interactivepaneltouchenabled');
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch interactive panels');
}

export async function getSmartTVs() {
  const data = await apiGet('/kvis/smarttvs');
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch smart TVs');
}

export async function getInternetConnected() {
  const data = await apiGet('/kvis/internetconnected');
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch internet connected schools');
}

export async function getTopSchoolsByICT(wise: string = 'computers', order: string = 'desc') {
  const data = await apiGet(`/kvis/topschoolsbyictinfrastructure?wise=${wise}&order=${order}`);
  if (data && data.success) {
    return data.data;
  }
  throw new Error(data?.message || 'Failed to fetch top schools by ICT');
}

// Green Building APIs

export interface GreenBuildingRegionRow {
  Sn: number;
  RegionName: string;
  TotalKVs: number;
  WithRainwaterHarvesting: number;
  WithSolarPowerPlant: number;
  TotalLEDLights: number;
  TotalSolarLights: number;
}

export interface GreenBuildingSummary {
  TotalKVs: number;
  TotalWithRainwater: number;
  TotalWithSolar: number;
  TotalLEDLights: number;
  TotalSolarLights: number;
}

export async function getGreenBuildingRegionwise() {
  const data = await apiGet('/kvis/greenbuildingregionwise');
  if (data && data.success) {
    return data.data as { summary: GreenBuildingSummary; regionWise: GreenBuildingRegionRow[] };
  }
  throw new Error(data?.message || 'Failed to fetch green building data');
}

// Library Profile Analysis APIs

export interface LibrarySummaryData {
  libraryCount: number;
  totalBooks: number;
  withAC: number;
  withProjector: number;
}

export interface LibrarySummaryResponse {
  Total: LibrarySummaryData;
  PMShree: LibrarySummaryData;
  NonPMShree: LibrarySummaryData;
}

export interface DigitalLibraryData {
  libraryCount: number;
  digitalBooks: number;
  automatedCount: number;
  withInternet: number;
  withAC: number;
  withProjector: number;
}

export interface DigitalLibraryResponse {
  Total: DigitalLibraryData;
  PMShree: DigitalLibraryData;
  NonPMShree: DigitalLibraryData;
}

export interface AutomatedSystemsData {
  libraryCount: number;
  automatedCount: number;
}

export interface AutomatedSystemsResponse {
  Summary: {
    Total: AutomatedSystemsData;
    PMShree: AutomatedSystemsData;
    NonPMShree: AutomatedSystemsData;
  };
  SoftwareBreakdown: {
    Total: Record<string, number>;
    PMShree: Record<string, number>;
    NonPMShree: Record<string, number>;
  };
}

export interface AvgBooksData {
  schoolCount: number;
  totalBooks: number;
  averageBooks: number;
}

export interface AvgBooksResponse {
  Total: AvgBooksData;
  PMShree: AvgBooksData;
  NonPMShree: AvgBooksData;
}

export interface BestLibrary {
  schoolName: string;
  schoolID: number;
  roName: string;
  districtName: string;
  stateName: string;
  isPMShree: boolean;
  totalBooksHindi: number;
  totalBooksEnglish: number;
  totalPhysicalBooks: number;
  totalDigitalBooks: number;
  libraryAutomationSystem: boolean;
  internateAvailableInLibrary: boolean;
  studentSeatingCapacity: number;
  score: number;
  label: string;
}

export interface LibraryUpgrade {
  schoolName: string;
  schoolID: number;
  roName: string;
  districtName: string;
  stateName: string;
  isPMShree: boolean;
  totalBooksHindi: number;
  totalBooksEnglish: number;
  totalPhysicalBooks: number;
  totalDigitalBooks: number;
  libraryAutomationSystem: boolean;
  internateAvailableInLibrary: boolean;
  studentSeatingCapacity: number;
  printerForBarCode: boolean;
  docScannerAvailable: boolean;
  projectorAvailable: boolean;
  noAC: number;
  noComputerStudent: number;
  needsUpgradeDetail: {
    automation: boolean;
    internet: boolean;
    barcodePrinter: boolean;
    scanner: boolean;
    projector: boolean;
    ac: boolean;
    computers: boolean;
  };
  score: number;
  label: string;
}

export async function getLibrarySummary(): Promise<LibrarySummaryResponse | null> {
  const data = await apiGet('/kvis/totalibrariesallkvs');
  if (data && data.success) {
    return data.data;
  }
  return null;
}

export async function getDigitalLibrarySummary(): Promise<DigitalLibraryResponse | null> {
  const data = await apiGet('/kvis/totaldigitallibrary');
  if (data && data.success) {
    return data.data;
  }
  return null;
}

export async function getAutomatedSystemsLibrary(): Promise<AutomatedSystemsResponse | null> {
  const data = await apiGet('/kvis/automatedsystemlibraries');
  if (data && data.success) {
    return data.data;
  }
  return null;
}

export async function getAvgBooksPerSchool(): Promise<AvgBooksResponse | null> {
  const data = await apiGet('/kvis/avgbooksperschool');
  if (data && data.success) {
    return data.data;
  }
  return null;
}

export async function getBestTopTenLibraries(): Promise<BestLibrary[] | null> {
  const data = await apiGet('/kvis/besttoptenlibrary');
  if (data && data.success && Array.isArray(data.data)) {
    // Transform the nested structure to flat structure
    return data.data.map((item: any) => ({
      schoolName: item.school?.schoolName,
      schoolID: item.school?.schoolID,
      roName: item.school?.roName,
      districtName: item.school?.districtName,
      stateName: item.school?.stateName,
      isPMShree: item.school?.isPMShree,
      totalBooksHindi: item.data?.totalBooksHindi || 0,
      totalBooksEnglish: item.data?.totalBooksEnglish || 0,
      totalPhysicalBooks: item.data?.totalPhysicalBooks || 0,
      totalDigitalBooks: item.data?.totalDigitalBooks || 0,
      libraryAutomationSystem: item.data?.libraryAutomationSystem || false,
      internateAvailableInLibrary: item.data?.internateAvailableInLibrary || false,
      studentSeatingCapacity: item.data?.studentSeatingCapacity || 0,
      score: item.score || 0,
      label: item.label || 'Total Physical Books'
    }));
  }
  return null;
}

export async function getLibrariesNeedingUpgrade(): Promise<LibraryUpgrade[] | null> {
  const data = await apiGet('/kvis/librariesneedingupgrade');
  if (data && data.success) {
    return data.data;
  }
  return null;
}

// CWSN Profile Analysis APIs

export interface CwsnStudentSummary {
  schoolCount: number;
  totalCwsn: number;
  primary: number;
  secondary: number;
}

export interface CwsnStudentSchool {
  schoolName: string;
  schoolID: number;
  roName: string;
  districtName: string;
  stateName: string;
  isPMShree: boolean;
  studentInPrimary: number;
  studentInSecondary: number;
  totalCwsnStudents: number;
  score: number;
  label: string;
}

export interface CwsnStudentResponse {
  summary: {
    Total: CwsnStudentSummary;
    PMShree: CwsnStudentSummary;
    NonPMShree: CwsnStudentSummary;
  };
  schools: CwsnStudentSchool[];
}

export interface CwsnTeacherSummary {
  schoolCount: number;
  totalTeachers: number;
  primaryTeachers: number;
  secondaryTeachers: number;
  totalTrainedTeachers: number;
}

export interface CwsnTeacherSchool {
  schoolName: string;
  schoolID: number;
  roName: string;
  districtName: string;
  stateName: string;
  isPMShree: boolean;
  teacherForPrimary: number;
  teacherForSecondary: number;
  totalTrainedTeachers: number;
  score: number;
  label: string;
}

export interface CwsnTeacherResponse {
  summary: {
    Total: CwsnTeacherSummary;
    PMShree: CwsnTeacherSummary;
    NonPMShree: CwsnTeacherSummary;
  };
  schools: CwsnTeacherSchool[];
}

export interface RampSummary {
  schoolCount: number;
  schoolsWithRamps: number;
  rampInGroundFloor: number;
  rampInAllFloor: number;
  noRamps: number;
}

export interface RampSchool {
  schoolName: string;
  schoolID: number;
  roName: string;
  districtName: string;
  stateName: string;
  isPMShree: boolean;
  rampInGroundFloor: boolean;
  rampInAllFloor: boolean;
  hasAnyRamp: boolean;
  score: number;
  label: string;
}

export interface RampResponse {
  summary: {
    Total: RampSummary;
    PMShree: RampSummary;
    NonPMShree: RampSummary;
  };
  schools: RampSchool[];
}

// Regional Grounds Interfaces
export interface RegionalGroundsSummary {
  totalGrounds: number;
  pmshri: {
    totalGrounds: number;
  };
  nonPmshri: {
    totalGrounds: number;
  };
}

export interface RegionalGroundsSchool {
  schoolName: string;
  schoolID: number;
  roName: string;
  stateName: string;
  districtName: string;
  isPMShree: number;
  totalRegionalGrounds: number;
}

export interface RegionalGroundsResponse {
  summary: RegionalGroundsSummary;
  schools: RegionalGroundsSchool[];
}

// National Grounds Interfaces
export interface NationalGroundsSummary {
  totalGrounds: number;
  pmshri: {
    totalGrounds: number;
  };
  nonPmshri: {
    totalGrounds: number;
  };
}

export interface NationalGroundsSchool {
  schoolName: string;
  schoolID: number;
  roName: string;
  stateName: string;
  districtName: string;
  isPMShree: number;
  totalNationalGrounds: number;
}

export interface NationalGroundsResponse {
  summary: NationalGroundsSummary;
  schools: NationalGroundsSchool[];
}

// Sports by Category API Response Types
export interface SportByCategoryItem {
  category: string;
  sportId: number;
  sportName: string;
  totalGrounds: number;
  pmShriGrounds: number;
  nonPmShriGrounds: number;
}

export interface SportsByCategorySummary {
  totalCategories: number;
  totalSports: number;
  totalGrounds: number;
  totalPmShri: number;
  totalNonPmShri: number;
}

export interface SportsByCategoryData {
  Other: SportByCategoryItem[];
  Ordinary: SportByCategoryItem[];
  Standard: SportByCategoryItem[];
}

export interface SportsByCategoryResponse {
  summary: SportsByCategorySummary;
  data: SportsByCategoryData;
}

// Quality Data API Response Types
export interface QualityGroundData {
  pmShriGrounds: number;
  nonPmShriGrounds: number;
  totalGrounds: number;
}

export interface QualityDataSummary {
  totalSchools: number;
  totalPmShriSchools: number;
  totalNonPmShriSchools: number;
  qualityStats: {
    standard: number;
    ordinary: number;
  };
  builtTypeStats: {
    indoor: number;
    outdoor: number;
  };
}

export interface QualityDataByType {
  'N/A': QualityGroundData;
  Clay: QualityGroundData;
  RCC: QualityGroundData;
  Synthetic: QualityGroundData;
  Wooden: QualityGroundData;
  Sinder: QualityGroundData;
  Turf: QualityGroundData;
}

export interface QualityDataResponse {
  summary: QualityDataSummary;
  data: QualityDataByType;
}

// Indoor/Outdoor Data API Response Types
export interface IndoorOutdoorCounts {
  pmshri: number;
  nonpmshri: number;
}

export interface IndoorOutdoorSportItem {
  category: string;
  sportId: number;
  sportName: string;
  totalGrounds: number;
  indoor: IndoorOutdoorCounts;
  outdoor: IndoorOutdoorCounts;
}

export interface IndoorOutdoorDataByCategory {
  Other: IndoorOutdoorSportItem[];
  Ordinary: IndoorOutdoorSportItem[];
  Standard: IndoorOutdoorSportItem[];
}

export interface IndoorOutdoorSummary {
  totalCategories: number;
  totalSports: number;
  totalSchools: number;
  totalPmShriSchools: number;
  totalNonPmShriSchools: number;
  indoor: IndoorOutdoorCounts;
  outdoor: IndoorOutdoorCounts;
}

export interface IndoorOutdoorResponse {
  summary: IndoorOutdoorSummary;
  data: IndoorOutdoorDataByCategory;
}

// Top Facility KVS API Response Types
export interface TopFacilityRecord {
  schoolId?: number;
  schoolName?: string;
  roName?: string;
  stateName?: string;
  isPMShree?: number;
  facilityType?: string;
  sportName?: string;
  qualityType?: string;
  builtType?: string;
  isRegionalLevel?: number;
  isNationalLevel?: number;
  grounds?: number;
  [key: string]: any;
}

export interface TopFacilitySummary {
  totalSchools: number;
  totalPmShriSchools: number;
  totalNonPmShriSchools: number;
}

export interface TopFacilityKvsResponse {
  summary: TopFacilitySummary;
  data: TopFacilityRecord[];
}

export async function getCwsnStudentSummary(): Promise<CwsnStudentResponse | null> {
  const data = await apiGet('/kvis/totalcwsnstudentsallkvs');
  if (data && data.success) {
    return data.data;
  }
  return null;
}

export async function getCwsnTeacherSummary(): Promise<CwsnTeacherResponse | null> {
  const data = await apiGet('/kvis/totalcwsntrainedteachers');
  if (data && data.success) {
    return data.data;
  }
  return null;
}

export async function getSchoolRampSummary(): Promise<RampResponse | null> {
  const data = await apiGet('/kvis/totalschoolswithramps');
  if (data && data.success) {
    return data.data;
  }
  return null;
}

// NCC Profile APIs - Using unified KPI endpoint

// Helper function to get NCC KPI data
async function getNCCKPIData(kpi: string) {
  const data = await apiGet(`/kvis/kvprofile/kpicatdatabyprofile?category=nccProfile&kpi=${kpi}`);
  if (data && data.success) {
    return data.data?.kpi_data || [];
  }
  throw new Error(data?.message || `Failed to fetch NCC ${kpi}`);
}

// Helper to count PM-SHRI vs Non PM-SHRI schools
function countByPMShri(kpiData: any[]) {
  let pmshriCount = 0;
  let nonPmshriCount = 0;

  kpiData.forEach((item: any) => {
    if (item.kvs_details && Array.isArray(item.kvs_details)) {
      item.kvs_details.forEach((school: any) => {
        if (school.isPMShree === 1) {
          pmshriCount++;
        } else {
          nonPmshriCount++;
        }
      });
    }
  });

  return { pmshriCount, nonPmshriCount };
}

export async function getNCCAvailability() {
  const kpiData = await getNCCKPIData('isNccAvailable');
  
  // Filter only schools where isNccAvailable = 1 (Yes)
  const schoolsWithNCC = kpiData.filter((item: any) => 
    item.kvs_details && item.kvs_details.some((school: any) => school.isNccAvailable === 1)
  );
  
  const allSchoolsWithNCC = schoolsWithNCC.flatMap((item: any) => 
    item.kvs_details.filter((school: any) => school.isNccAvailable === 1)
  );
  
  const pmshriWithNCC = allSchoolsWithNCC.filter((s: any) => s.isPMShree === 1).length;
  const nonPmshriWithNCC = allSchoolsWithNCC.filter((s: any) => s.isPMShree !== 1).length;

  return {
    PMSHRI: { totalWithNCC: pmshriWithNCC },
    nonPMSHRI: { totalWithNCC: nonPmshriWithNCC },
    total: pmshriWithNCC + nonPmshriWithNCC,
  };
}

export async function getNCCByGroup() {
  const kpiData = await getNCCKPIData('nccGroup');
  
  // nccGroup: 1 = Army, 2 = Navy, 3 = Air Force
  const groupMap: any = { 1: 'Army', 2: 'Navy', 3: 'Air Force' };
  const result: any = {
    Army: { PMSHRI: 0, nonPMSHRI: 0 },
    Navy: { PMSHRI: 0, nonPMSHRI: 0 },
    'Air Force': { PMSHRI: 0, nonPMSHRI: 0 },
  };

  kpiData.forEach((item: any) => {
    const groupName = groupMap[item.nccGroup];
    if (groupName && item.kvs_details) {
      item.kvs_details.forEach((school: any) => {
        if (school.isPMShree === 1) {
          result[groupName].PMSHRI++;
        } else {
          result[groupName].nonPMSHRI++;
        }
      });
    }
  });

  return result;
}

export async function getNCCByWing() {
  const kpiData = await getNCCKPIData('avaliableWing');
  
  // avaliableWing: 1 = Junior Division, 2 = Senior Division
  const wingMap: any = { 1: 'Junior Division', 2: 'Senior Division' };
  const result: any = {
    'Junior Division': { PMSHRI: 0, nonPMSHRI: 0 },
    'Senior Division': { PMSHRI: 0, nonPMSHRI: 0 },
  };

  kpiData.forEach((item: any) => {
    const wingName = wingMap[item.avaliableWing];
    if (wingName && item.kvs_details) {
      item.kvs_details.forEach((school: any) => {
        if (school.isPMShree === 1) {
          result[wingName].PMSHRI++;
        } else {
          result[wingName].nonPMSHRI++;
        }
      });
    }
  });

  return result;
}

export async function getNCCOfficers() {
  const kpiData = await getNCCKPIData('anoCto');
  
  // anoCto: 1 = ANO, 2 = CTO
  const result: any = {
    ANO: { total: 0, PMSHRI: 0, nonPMSHRI: 0 },
    CTO: { total: 0, PMSHRI: 0, nonPMSHRI: 0 },
  };

  kpiData.forEach((item: any) => {
    if (item.kvs_details) {
      item.kvs_details.forEach((school: any) => {
        if (school.anoCto === 1) {
          result.ANO.total++;
          if (school.isPMShree === 1) {
            result.ANO.PMSHRI++;
          } else {
            result.ANO.nonPMSHRI++;
          }
        } else if (school.anoCto === 2) {
          result.CTO.total++;
          if (school.isPMShree === 1) {
            result.CTO.PMSHRI++;
          } else {
            result.CTO.nonPMSHRI++;
          }
        }
      });
    }
  });

  return result;
}

export async function getNCCByRank() {
  const kpiData = await getNCCKPIData('anoCtoRank');
  
  // Return rank-wise distribution
  const result: any = {};

  kpiData.forEach((item: any) => {
    const rank = item.anoCtoRank || 'Unknown';
    if (!result[rank]) {
      result[rank] = { PMSHRI: 0, nonPMSHRI: 0, total: 0 };
    }

    if (item.kvs_details) {
      item.kvs_details.forEach((school: any) => {
        result[rank].total++;
        if (school.isPMShree === 1) {
          result[rank].PMSHRI++;
        } else {
          result[rank].nonPMSHRI++;
        }
      });
    }
  });

  return result;
}

export async function getTopSchoolsByNCC() {
  const kpiData = await getNCCKPIData('isNccAvailable');
  
  // Get all schools with NCC
  const allSchoolsWithNCC = kpiData
    .filter((item: any) => item.kvs_details && item.kvs_details.length > 0)
    .flatMap((item: any) => item.kvs_details)
    .filter((school: any) => school.isNccAvailable === 1);

  // Calculate NCC score based on cadets
  const schoolsWithScores = allSchoolsWithNCC.map((school: any) => ({
    schoolID: school.schoolID,
    schoolName: school.schoolName,
    location: `${school.city}, ${school.stateName}`,
    status: school.isPMShree === 1 ? 'PM-SHRI' : 'Non PM-SHRI',
    nccGroup: school.nccGroup === 1 ? 'Army' : school.nccGroup === 2 ? 'Navy' : school.nccGroup === 3 ? 'Air Force' : '-',
    nccWing: school.avaliableWing === 1 ? 'Junior Division' : school.avaliableWing === 2 ? 'Senior Division' : '-',
    officer: school.anoCto === 1 ? 'ANO' : school.anoCto === 2 ? 'CTO' : '-',
    totalCadets: (school.totalBoyCadet || 0) + (school.totalGirlCadet || 0),
    nccScore: (school.totalBoyCadet || 0) + (school.totalGirlCadet || 0),
    nccGroupRaw: school.nccGroup,
    nccWingRaw: school.avaliableWing,
    officerRaw: school.anoCto,
  }));

  // Sort by score
  const sortedSchools = schoolsWithScores.sort((a: any, b: any) => b.nccScore - a.nccScore);

  // Top schools overall
  const topSchoolsAll = sortedSchools.slice(0, 10);

  // Top schools by group
  const armySchools = sortedSchools.filter((s: any) => s.nccGroupRaw === 1).slice(0, 10);
  const navySchools = sortedSchools.filter((s: any) => s.nccGroupRaw === 2).slice(0, 10);
  const airForceSchools = sortedSchools.filter((s: any) => s.nccGroupRaw === 3).slice(0, 10);

  // Top schools by wing
  const juniorDivisionSchools = sortedSchools.filter((s: any) => s.nccWingRaw === 1).slice(0, 10);
  const seniorDivisionSchools = sortedSchools.filter((s: any) => s.nccWingRaw === 2).slice(0, 10);

  // Top schools by officer
  const anoSchools = sortedSchools.filter((s: any) => s.officerRaw === 1).slice(0, 10);
  const ctoSchools = sortedSchools.filter((s: any) => s.officerRaw === 2).slice(0, 10);

  return {
    all: topSchoolsAll,
    byGroup: {
      army: armySchools,
      navy: navySchools,
      airForce: airForceSchools,
    },
    byWing: {
      juniorDivision: juniorDivisionSchools,
      seniorDivision: seniorDivisionSchools,
    },
    byOfficer: {
      ano: anoSchools,
      cto: ctoSchools,
    },
  };
}

// Sports Profile APIs

export interface SportInfraRecord {
  /** numeric key matching SPORT_TITLE, e.g. '4' = Badminton Court */
  sportType: string;
  /** 'Standard' | 'Ordinary' – matches SPORT_CATEGORY */
  category: string;
  /** 'Indoor' | 'Outdoor' – matches SPORT_TYPE */
  type: string;
  /** 'Clay' | 'RCC' | 'Synthetic' | 'Wooden' | 'Turf' | 'Sinder' – matches SPORT_QUALITY */
  quality: string;
  /** whether the facility is used for regional-level competitions */
  forRegional: boolean;
  /** whether the facility is used for national-level competitions */
  forNational: boolean;
  /** count of units */
  quantity: number;
  isPMShree: number;
  schoolID?: number;
  schoolName?: string;
  roName?: string;
  stateName?: string;
}

export interface SportsSummaryResponse {
  PMSHRI: {
    totalFacilities: number;
    totalQuantity: number;
    forRegional: number;
    forNational: number;
  };
  nonPMSHRI: {
    totalFacilities: number;
    totalQuantity: number;
    forRegional: number;
    forNational: number;
  };
  breakdown: SportInfraRecord[];
}

export async function getSportsInfra(): Promise<SportsSummaryResponse | null> {
  const data = await apiGet('/kvis/sportsinfrastructure');
  if (data && data.success) {
    return data.data;
  }
  return null;
}

export async function getSportsInfraByKPI(kpi: string): Promise<any> {
  const data = await apiGet(`/kvis/kvprofile/kpicatdatabyprofile?category=sportsProfile&kpi=${kpi}`);
  if (data && data.success) {
    return data.data?.kpi_data || [];
  }
  throw new Error(data?.message || `Failed to fetch sports KPI: ${kpi}`);
}

// Safety APIs

export interface SafetySchoolRow {
  Sn: number;
  School: { ID: number; Name: string };
  State: string;
  RO: { ID: number; Name: string };
  BuildingSafety: {
    CertificateAvailable: 'YES' | 'NO';
    IssuingAgency: string | null;
    ValidFrom: string | null;
    ValidTo: string | null;
  };
  FireSafety: {
    CertificateAvailable: 'YES' | 'NO';
    IssuingAgency: string | null;
    ValidFrom: string | null;
    ValidTo: string | null;
  };
}

export interface SafetySummary {
  TotalSchools: number;
  BuildingCertified: number;
  FireCertified: number;
  BothCertified: number;
  NeitherCertified: number;
}

export async function getSafetySchoolwise(): Promise<{ summary: SafetySummary; schools: SafetySchoolRow[] } | null> {
  const data = await apiGet('/kvis/safetyschoolwise');
  if (data && data.success) {
    return data.data as { summary: SafetySummary; schools: SafetySchoolRow[] };
  }
  return null;
}